import { useQuery } from 'react-query';

import { useCompanyId } from 'modules/app/hooks/useCompanyId';
import { getV2ApiCompanyIdTravelPropertiesPropertyOfferIdOffers } from 'src/client/spendesk-api';
import { type GetV2ApiCompanyIdTravelPropertiesPropertyOfferIdOffersResponseOK } from 'src/client/spendesk-api-types';

import {
  type Amenity,
  type Location,
  type PropertyDetails,
  type RateOption,
  type Room,
} from '../pages/PropertyDetailsPage/types';

export const usePropertyDetailsQuery = (propertyId: string) => {
  const companyId = useCompanyId();
  return useQuery(
    ['property', propertyId],
    () =>
      getV2ApiCompanyIdTravelPropertiesPropertyOfferIdOffers({
        path: { companyId, propertyOfferId: propertyId },
      }),
    {
      enabled: Boolean(propertyId),
    },
  );
};

const reshapeLocation = (
  location: GetV2ApiCompanyIdTravelPropertiesPropertyOfferIdOffersResponseOK['data']['result']['location'],
): Location => {
  return {
    address: location.address,
    region: { name: location.region?.name ?? '' },
    country: location.country,
    coordinates: location.coordinates,
  };
};

const reshapeRateOption = (
  rateOption: GetV2ApiCompanyIdTravelPropertiesPropertyOfferIdOffersResponseOK['data']['result']['rooms'][number]['rateOptions'][number],
): RateOption => {
  return {
    id: rateOption.id,
    supplierRateId: rateOption.supplierRateId,
    paymentType: rateOption.paymentType,
    totalPrice: rateOption.totalPrice,
    basePrice: rateOption.basePrice,
    tax: rateOption.tax,
    fees: rateOption.fees,
    mealInformation: {
      boardType:
        rateOption.mealInformation.boardType === 'all_inclusive'
          ? 'full_board'
          : (rateOption.mealInformation.boardType ?? 'room_only'),
    },
    refundInformation: {
      type:
        rateOption.refundInformation.type === 'partially_refundable'
          ? 'refundable'
          : rateOption.refundInformation.type,
      cancellationTimeline:
        rateOption.refundInformation.cancellationTimeline.map((timeline) => ({
          deadline: timeline.before,
          amount: timeline.refundAmount,
        })),
    },
  };
};

const reshapeRoom = (
  room: GetV2ApiCompanyIdTravelPropertiesPropertyOfferIdOffersResponseOK['data']['result']['rooms'][number],
): Room | null => {
  if (!room.nightlyBaseFrom) {
    return null;
  }
  return {
    name: room.name,
    photos: room.photos,
    beds: room.beds,
    nightlyBaseFrom: room.nightlyBaseFrom,
    rateOptions: room.rateOptions.map(reshapeRateOption),
  };
};

const reshapeRooms = (
  rooms: GetV2ApiCompanyIdTravelPropertiesPropertyOfferIdOffersResponseOK['data']['result']['rooms'],
): Room[] => {
  return rooms.map(reshapeRoom).filter((room): room is Room => room !== null);
};

const reshapeAmenity = (
  amenity: GetV2ApiCompanyIdTravelPropertiesPropertyOfferIdOffersResponseOK['data']['result']['amenities'][number],
): Amenity => {
  return {
    type: amenity.type as Amenity['type'],
    description: amenity.description,
  };
};

export const reshapePropertyDetails = (
  data: GetV2ApiCompanyIdTravelPropertiesPropertyOfferIdOffersResponseOK['data']['result'],
): PropertyDetails => {
  return {
    propertyOfferId: data.propertyOfferId,
    supplier: data.supplier,
    name: data.name,
    description: data.description ?? '',
    location: reshapeLocation(data.location),
    rating: data.rating,
    amenities: data.amenities.map(reshapeAmenity),
    photos: data.photos,
    bookingContext: {
      checkInDate: new Date(data.bookingContext.checkInDate),
      checkOutDate: new Date(data.bookingContext.checkOutDate),
      roomsCount: data.bookingContext.roomsCount,
      adultsCount: data.bookingContext.adultsCount,
    },
    rooms: reshapeRooms(data.rooms),
  };
};
